import FuzzySet from "fuzzyset";

import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { ScenarioConfiguration, VersionScenarioConfiguration } from "fond/types/version";
import { safeJSONParse } from "fond/utils/json";

import { c2sFileMap } from "./files";
import { FormData } from "./SetupForm";

export const ignoreLayerOption = {
  Type: "LAYER",
  ID: undefined,
  Key: undefined,
  Label: "Ignore Layer",
} as unknown as LayerConfig;

/**
 * File type identifiers for C2S artifacts
 */
export type C2SArtifactFileType = "layersConfiguration" | "costBreakdown" | "bom";

/**
 * Expected file configuration with matching metadata
 */
export interface ExpectedFileConfig {
  id: C2SArtifactFileType;
  fileName: string;
  extension: string;
  baseName: string;
  keywords: string[];
}

/**
 * Expected file configurations with their base names, extensions, and keywords
 */
export const EXPECTED_FILES: ExpectedFileConfig[] = c2sFileMap.ArtifactFiles.map((fileConfig) => {
  const lastDotIndex = fileConfig.file.lastIndexOf(".");

  // Define keywords for each file type to help with matching
  const keywords: string[] = [];
  if (fileConfig.id === "bom") {
    keywords.push("bom", "bill", "materials", "bill_of_materials", "billofmaterials");
  } else if (fileConfig.id === "costBreakdown") {
    keywords.push("cost", "breakdown", "cost_breakdown", "costbreakdown");
  } else if (fileConfig.id === "layersConfiguration") {
    keywords.push("layers", "configuration", "config", "layers_configuration", "layersconfiguration");
  }

  return {
    id: fileConfig.id as C2SArtifactFileType,
    fileName: fileConfig.file,
    extension: fileConfig.file.slice(lastDotIndex),
    baseName: fileConfig.file.slice(0, lastDotIndex),
    keywords,
  };
});

/**
 * Uses fuzzy matching and keyword matching to find the best match for an uploaded file.
 * File extensions must match exactly. Base names are matched using:
 * 1. Fuzzy string matching against expected filenames
 * 2. Keyword matching - checking if identifying terms appear in the filename
 *
 * @param uploadedFileName - The name of the file uploaded by the user
 * @returns The matched file configuration or null if no match found
 *
 * @example
 * fuzzyMatchFile("highlands_broadband_BOM_v1.0.csv") // Returns { id: "bom", fileName: "bom.csv" }
 * fuzzyMatchFile("layers-configuration.json") // Returns { id: "layersConfiguration", fileName: "layers_configuration.json" }
 * fuzzyMatchFile("cost_breakdown_2024.csv") // Returns { id: "costBreakdown", fileName: "cost_breakdown.csv" }
 */
export const fuzzyMatchFile = (uploadedFileName: string): { id: C2SArtifactFileType; fileName: string } | null => {
  const lastDotIndex = uploadedFileName.lastIndexOf(".");

  if (lastDotIndex === -1) return null;

  const uploadedExtension = uploadedFileName.slice(lastDotIndex).toLowerCase();
  const uploadedBaseName = uploadedFileName.slice(0, lastDotIndex).toLowerCase();

  // Filter by extension first (must match, case-insensitive)
  const matchingExtension = EXPECTED_FILES.filter((f) => f.extension.toLowerCase() === uploadedExtension);

  if (matchingExtension.length === 0) return null;

  let bestMatch: ExpectedFileConfig | null = null;
  let bestScore = 0;

  for (const expectedFile of matchingExtension) {
    // Strategy 1: Fuzzy match against the full expected base name
    const fuzzySet = FuzzySet([expectedFile.baseName]);
    const fuzzyMatches = fuzzySet.get(uploadedBaseName);
    const fuzzyScore = fuzzyMatches?.[0]?.[0] || 0;

    // Strategy 2: Check if any keywords appear in the uploaded filename
    // This helps match files like "highlands_broadband_BOM_v1.0.csv" to "bom"
    let keywordScore = 0;
    for (const keyword of expectedFile.keywords) {
      if (uploadedBaseName.includes(keyword.toLowerCase())) {
        // Give higher score if keyword appears as a separate word/segment
        const isWordBoundary =
          new RegExp(`(^|[_\\-\\s])${keyword}([_\\-\\s]|$)`, "i").test(uploadedBaseName) || uploadedBaseName === keyword.toLowerCase();

        keywordScore = Math.max(keywordScore, isWordBoundary ? 0.9 : 0.7);
      }
    }

    // Use the better of fuzzy or keyword score
    const score = Math.max(fuzzyScore, keywordScore);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = expectedFile;
    }
  }

  // Accept if score is above threshold (0.4 = 40% similarity)
  if (bestScore < 0.4 || !bestMatch) return null;

  return { id: bestMatch.id, fileName: bestMatch.fileName };
};

export interface C2SLayersConfiguration {
  physical_elements?: string[] | null;
  network_elements?: string[] | null;
  edge_elements?: string[] | null;
  node_elements?: string[] | null;
  layer_name_mapping?: Record<string, string> | null;
  combine_layers?: Record<string, string[]> | null;
}

export const resolveLayerConfigurationNameFromFilename = (layersConfiguration: C2SLayersConfiguration, fileName: string): string => {
  return layersConfiguration?.layer_name_mapping?.[fileName] || fileName;
};

export const resolveLayerConfigurationInputLayers = (layersConfiguration: C2SLayersConfiguration): Set<string> => {
  let layers = new Set<string>();
  layersConfiguration?.physical_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.network_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.edge_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.node_elements?.forEach((el) => layers.add(el));

  // The BOM processes layer configurations by (i) renaming, and then (ii) combining layers. We're trying
  // to do the reverse here, so we first split up combined layers and then revert back to the original name.
  if (layersConfiguration?.combine_layers) {
    layers = new Set(Array.from(layers).flatMap((layer) => layersConfiguration?.combine_layers?.[layer] ?? [layer]));
  }

  if (layersConfiguration?.layer_name_mapping) {
    const reverseNameMap = new Map<string, string>(
      Object.entries(layersConfiguration.layer_name_mapping).map(([original, renamed]) => [renamed, original])
    );
    layers = new Set(Array.from(layers).map((layer) => reverseNameMap.get(layer) ?? layer));
  }

  return layers;
};

/**
 * Filters out null/undefined/ignoreLayerOption values from a layer mapping.
 */
export const filterLayerMappings = (mapping: Record<string, LayerConfig | null>): Record<string, LayerConfig> => {
  return Object.fromEntries(Object.entries(mapping).filter(([_, value]) => value != null && value !== ignoreLayerOption)) as Record<
    string,
    LayerConfig
  >;
};

export const convertLayerMappings = (mapping: Record<string, LayerConfig | null>): FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"] => {
  return Object.entries(filterLayerMappings(mapping)).map(([key, { Key }]) => ({
    Name: key,
    Layer: { LayerKey: Key },
  }));
};

/**
 * Converts a VersionScenarioConfiguration to FormData format.
 * Specifically:
 * 1) Parses Inputs.ToolLayersConfiguration.LayersConfiguration.Data from JSON string to object
 * 2) Stringifies ToolConfig.CostToServe.AdditionalSettings from object to JSON string
 * 3) Stringifies ToolConfig.DesignPhasePartitioner.AdditionalSettings from object to JSON string
 * 4) Adds Files field for form compatibility
 */
export const convertConfigToFormData = (config: VersionScenarioConfiguration): FormData => {
  return {
    Files: {
      Artifacts: [],
      Occupancy: null,
    },
    Inputs: {
      ...config.Inputs,
      ToolLayersConfiguration: {
        ...config.Inputs.ToolLayersConfiguration,
        LayersConfiguration: {
          ...config.Inputs.ToolLayersConfiguration.LayersConfiguration,
          Data: safeJSONParse(config.Inputs.ToolLayersConfiguration.LayersConfiguration.Data || ""),
        },
      },
    },
    ToolConfigurations: {
      ...config.ToolConfigurations,
      CostToServe: {
        ...config.ToolConfigurations.CostToServe,
        AdditionalSettings: JSON.stringify(config.ToolConfigurations.CostToServe.AdditionalSettings),
      },
      DesignPhasePartitioner: {
        ...config.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: JSON.stringify(config.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings),
      },
    },
  };
};

/**
 * Converts FormData to ScenarioConfiguration format.
 * Specifically:
 * 1) Stringifies Inputs.ToolLayersConfiguration.LayersConfiguration.Data from object to JSON string
 * 2) Parses ToolConfig.CostToServe.AdditionalSettings from JSON string to object
 * 3) Parses ToolConfig.DesignPhasePartitioner.AdditionalSettings from JSON string to object
 * 4) Removes Files field (not part of ScenarioConfiguration)
 */
export const convertFormDataToConfig = (formData: FormData): ScenarioConfiguration => {
  return {
    Inputs: {
      ...formData.Inputs,
      ToolLayersConfiguration: {
        ...formData.Inputs.ToolLayersConfiguration,
        LayersConfiguration: {
          ...formData.Inputs.ToolLayersConfiguration.LayersConfiguration,
          Data: JSON.stringify(formData.Inputs.ToolLayersConfiguration.LayersConfiguration.Data),
        },
      },
    },
    ToolConfigurations: {
      ...formData.ToolConfigurations,
      CostToServe: {
        ...formData.ToolConfigurations.CostToServe,
        AdditionalSettings: safeJSONParse(formData.ToolConfigurations.CostToServe.AdditionalSettings),
      },
      DesignPhasePartitioner: {
        ...formData.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: safeJSONParse(formData.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings),
      },
    },
  };
};
